import { Alert, Form, Modal, Typography } from 'antd';

import { TimeInput } from '@app/shared/components';

import {
  useEmployeeWorkScheduleActionModal,
  useEmployeeWorkScheduleActionModalEffects,
} from '../hooks';
import { getTimeEndRules, getTimeStartRules } from '../utils';

import '../styles/EmployeeWorkScheduleActionModal.scss';

import type { FC } from 'react';

interface EmployeeWorkScheduleActionModalProps {
  employeeId: number;
  employeeName: string;
  initialTimeS: string;
  initialTimeE: string;
  initialWarningText?: string;
  isOpen: boolean;
  mode: 'edit' | 'delete';
  workScheduleId: number;
  onClose(): void;
}

const { Text } = Typography;

export const EmployeeWorkScheduleActionModal: FC<
  EmployeeWorkScheduleActionModalProps
> = ({
  employeeId,
  employeeName,
  initialTimeS,
  initialTimeE,
  initialWarningText,
  isOpen,
  mode,
  workScheduleId,
  onClose,
}) => {
  const {
    form,
    warningText,
    currentScheduleLabel,
    selectedDateLabel,
    isLoading,
    handleClose,
    handleConfirm,
    handleFinish,
    syncFormAndCheckScheduleConflicts,
  } = useEmployeeWorkScheduleActionModal({
    employeeId,
    employeeName,
    initialTimeS,
    initialTimeE,
    initialWarningText,
    isOpen,
    mode,
    workScheduleId,
    onClose,
  });

  useEmployeeWorkScheduleActionModalEffects({
    syncFormAndCheckScheduleConflicts,
  });

  const isEditMode = mode === 'edit';

  return (
    <Modal
      className="EmployeeWorkScheduleActionModal"
      open={isOpen}
      destroyOnHidden
      width={520}
      onCancel={handleClose}
      onOk={handleConfirm}
      okText={isEditMode ? 'Сохранить' : 'Удалить'}
      cancelText="Отмена"
      title={
        <div className="EmployeeWorkScheduleActionModal__title">
          <span className="EmployeeWorkScheduleActionModal__title-text">
            {isEditMode ? 'Изменить расписание' : 'Отменить рабочий день'}
          </span>
          <span className="EmployeeWorkScheduleActionModal__title-hint">
            {isEditMode
              ? 'Скорректируйте рабочее время исполнителя на выбранную дату'
              : 'Подтвердите удаление рабочего дня исполнителя из расписания'}
          </span>
        </div>
      }
      data-testid={
        isEditMode
          ? 'calendar-edit-workday-popup'
          : 'calendar-delete-workday-popup'
      }
      okButtonProps={{
        loading: isLoading,
        danger: !isEditMode,
        'data-testid': isEditMode ? 'save' : 'delete',
      }}
      cancelButtonProps={{ disabled: isLoading, 'data-testid': 'cancel' }}
    >
      <div className="EmployeeWorkScheduleActionModal__content">
        <div className="EmployeeWorkScheduleActionModal__summary">
          <div className="EmployeeWorkScheduleActionModal__summary-card">
            <Text className="EmployeeWorkScheduleActionModal__summary-label">
              Исполнитель
            </Text>
            <Text className="EmployeeWorkScheduleActionModal__summary-value">
              {employeeName}
            </Text>
          </div>

          <div className="EmployeeWorkScheduleActionModal__summary-card">
            <Text className="EmployeeWorkScheduleActionModal__summary-label">
              Дата
            </Text>
            <Text className="EmployeeWorkScheduleActionModal__summary-value">
              {selectedDateLabel}
            </Text>
          </div>
        </div>

        {isEditMode ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            className="EmployeeWorkScheduleActionModal__form"
          >
            <div className="EmployeeWorkScheduleActionModal__time-grid">
              <Form.Item
                name="timeS"
                label="От"
                rules={getTimeStartRules()}
                rootClassName="EmployeeWorkScheduleActionModal__time-form-item"
              >
                <TimeInput data-testid="time-start" />
              </Form.Item>

              <Form.Item
                name="timeE"
                label="До"
                dependencies={['timeS']}
                rules={getTimeEndRules()}
                rootClassName="EmployeeWorkScheduleActionModal__time-form-item"
              >
                <TimeInput data-testid="time-end" />
              </Form.Item>
            </div>
          </Form>
        ) : (
          <div className="EmployeeWorkScheduleActionModal__time-grid">
            <div className="EmployeeWorkScheduleActionModal__delete-card">
              <Text className="EmployeeWorkScheduleActionModal__summary-label">
                Текущее расписание
              </Text>
              <Text className="EmployeeWorkScheduleActionModal__summary-value">
                {currentScheduleLabel}
              </Text>
            </div>
          </div>
        )}

        {!!warningText && (
          <Alert
            className="EmployeeWorkScheduleActionModal__warning"
            message={warningText}
            type="warning"
            showIcon
          />
        )}
      </div>
    </Modal>
  );
};
